import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatINR } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Tabs, type TabDef } from '../components/Tabs.js';
import { useConfirm } from '../components/Confirm.js';

interface Payee { payee_type: string; payee_id: number; payee_name: string | null; is_staff: boolean; investment_amount: string; pending_investment_amount: string; accrued: string; paid: string; balance: string; }
interface MonthlyRow {
  month: string; investments: number; customers: number;
  investment_amount: number; accrued: number; paid: number; balance: number;
  as_referrer: number; as_enroller: number;
}
interface Accrual { application_id: number; application_no: string; customer: string; customer_code: string; series_code: string; date_money_received: string | null; investment_amount: string; incentive_amount: string; paid: boolean; }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(d: string | null): string {
  if (!d) return '—';
  const [y, m] = d.slice(0, 7).split('-');
  return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
}
interface AgentRow { id: number; full_name: string; agent_code: string; commission_status: string; commission_rate_pct: number | null; }

export function IncentivesPage() {
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const canRevert = user?.role === 'super_admin';
  const [msg, setMsg] = useState('');
  const [balTab, setBalTab] = useState<'staff' | 'agent'>('staff');
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Month for the Excel download. Defaults to this month; the owner picks
   *  the one they are paying out for. */
  const [repMonth, setRepMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const overview = useQuery({ queryKey: ['inc-overview'], queryFn: () => api.get<{ rows: Payee[] }>('/api/incentives/overview') });
  const agents = useQuery({ queryKey: ['inc-agents'], queryFn: () => api.get<{ rows: AgentRow[] }>('/api/incentives/agents'), enabled: can('incentives:manage-eligibility') });
  const [rate, setRate] = useState<Record<number, string>>({});
  const grantAgent = useMutation({
    mutationFn: (v: { id: number; rate_pct: number }) => api.post(`/api/incentives/agents/${v.id}/eligibility`, { rate_pct: v.rate_pct }),
    onSuccess: () => { setMsg('Agent commission sent for approval.'); qc.invalidateQueries({ queryKey: ['inc-agents'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  const revokeAgent = useMutation({
    mutationFn: (id: number) => api.post(`/api/incentives/agents/${id}/eligibility/revoke`, {}),
    onSuccess: () => { setMsg('Agent commission revoked.'); qc.invalidateQueries({ queryKey: ['inc-agents'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-bold tracking-tight m-0">Incentives &amp; commissions</h1>
        {/* One month's incentives for everyone, staff and agents, as a
            spreadsheet (owner 2026-09-14). Same endpoint the Reports page
            links to — one URL, so the two places cannot drift apart. */}
        {can('incentives:manage-eligibility') && (
          <div className="ml-auto flex items-center gap-2">
            <input
              type="month"
              className="px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary"
              value={repMonth}
              onChange={(e) => setRepMonth(e.target.value)} />
            <a href={`/api/incentives/monthly-report.xlsx?month=${repMonth}`}
               className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg"
               title="Every incentive earned in this month — staff and agents on separate sheets, with the investment behind each figure">
              ↓ Download month (Excel)
            </a>
          </div>
        )}
      </div>
      <p className="text-sm text-text-muted mt-1 mb-5">Balances owed to staff and agents.</p>
      {msg && <div className="text-xs text-primary mb-3">{msg}</div>}

      <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Balances</h2>
      {(() => {
        const all = overview.data?.rows ?? [];
        // Staff tab = internal staff (users flagged is_staff). A user-payee whose
        // is_staff flag is off (a CXO or an agent-role user) is an external
        // earner → Agent tab, matching the segments split.
        const isStaff = (p: Payee) => p.is_staff === true;
        const staffRows = all.filter(isStaff);
        const agentRows = all.filter((p) => !isStaff(p));
        const balTabs: TabDef<'staff' | 'agent'>[] = [
          { key: 'staff', label: 'Staff', count: staffRows.length },
          { key: 'agent', label: 'Agent', count: agentRows.length },
        ];
        const shown = balTab === 'staff' ? staffRows : agentRows;
        return (
        <>
        <Tabs tabs={balTabs} active={balTab} onChange={setBalTab} />
        <div className="mb-6">
        <DataTable
          columns={[
            { key: 'payee', header: 'Payee', value: (p) => p.payee_name ?? `${p.payee_type} #${p.payee_id}`,
              render: (p) => (
                <span>
                  <span className="font-medium">{p.payee_name ?? `${p.payee_type} #${p.payee_id}`}</span>{' '}
                  <span className="text-xs text-text-muted">{p.is_staff ? 'Staff' : 'Agent'}</span>
                </span>
              ) },
            { key: 'investment_amount', header: 'Investment', align: 'right', value: (p) => Number(p.investment_amount), render: (p) => <span className="mono">{formatINR(p.investment_amount)}</span> },
            { key: 'accrued', header: 'Accrued', align: 'right', value: (p) => Number(p.accrued), render: (p) => <span className="mono">{formatINR(p.accrued)}</span> },
            { key: 'paid', header: 'Paid', align: 'right', value: (p) => Number(p.paid), render: (p) => <span className="mono text-text-muted">{formatINR(p.paid)}</span> },
            { key: 'balance', header: 'Balance', align: 'right', value: (p) => Number(p.balance), render: (p) => <span className="mono font-semibold">{formatINR(p.balance)}</span> },
            // Which investment that balance is owed ON (owner 2026-08-03). The
            // Balance column says ₹2,02,000 is due; this says it is due on
            // ₹1,01,00,000 of business. Summed from the actual unpaid rows, so
            // it stays right when a payee's book mixes incentive rates.
            { key: 'pending_investment_amount', header: 'Pending for', align: 'right',
              value: (p) => Number(p.pending_investment_amount),
              render: (p) => Number(p.pending_investment_amount) > 0
                ? <span className="mono" title="Investment whose incentive is still unpaid">{formatINR(p.pending_investment_amount)}</span>
                : <span className="text-text-muted">—</span> },
            { key: 'actions', header: '', sortable: false, filterable: false, align: 'right',
              render: (p) => {
                const key = `${p.payee_type}-${p.payee_id}`;
                return (
                  <div className="flex gap-2 justify-end items-center whitespace-nowrap">
                    <a href={`/api/incentives/payees/${p.payee_type}/${p.payee_id}/statement.pdf`} target="_blank" rel="noreferrer" className="text-xs text-text-muted hover:text-primary">PDF</a>
                    <button onClick={() => setExpanded(expanded === key ? null : key)} className="text-xs text-primary hover:underline">
                      {expanded === key ? 'Hide' : 'By customer ▾'}
                    </button>
                  </div>
                );
              } },
          ] as Column<Payee>[]}
          rows={shown}
          rowKey={(p) => `${p.payee_type}-${p.payee_id}`}
          defaultSort={{ key: 'balance', dir: 'desc' }}
          empty={balTab === 'staff' ? 'No staff accruals yet.' : 'No agent accruals yet.'}
          renderExpanded={(p) => expanded === `${p.payee_type}-${p.payee_id}`
            ? <PayeeAccruals p={p} canPay={can('incentives:pay')} canRevert={canRevert}
                onPaid={() => { setMsg('Incentive paid.'); qc.invalidateQueries({ queryKey: ['inc-overview'] }); }}
                onReverted={() => { setMsg('Payment reverted.'); qc.invalidateQueries({ queryKey: ['inc-overview'] }); }} />
            : null}
        />
        </div>
        </>
        );
      })()}

      {can('incentives:manage-eligibility') && (
        <>
          <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Agent commissions</h2>
          <div className="bg-surface border border-border rounded-lg shadow-card divide-y divide-border mb-6">
            {(agents.data?.rows ?? []).map((ag) => (
              <div key={ag.id} className="p-4 flex items-center gap-3 text-sm flex-wrap">
                <span className="font-medium">{ag.full_name}</span>
                <span className="font-mono text-xs text-text-muted">{ag.agent_code}</span>
                <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{ag.commission_status}{ag.commission_rate_pct != null ? ` · ${ag.commission_rate_pct}%` : ''}</span>
                <div className="ml-auto flex gap-2 items-center">
                  {ag.commission_status !== 'Approved' && (
                    <>
                      <input className="w-20 px-2 py-1 text-xs border border-border-strong rounded" placeholder="rate %"
                        value={rate[ag.id] ?? ''} onChange={(e) => setRate((s) => ({ ...s, [ag.id]: e.target.value }))} />
                      <button disabled={!rate[ag.id] || Number(rate[ag.id]) <= 0 || grantAgent.isPending}
                        onClick={() => { setMsg(''); grantAgent.mutate({ id: ag.id, rate_pct: Number(rate[ag.id]) }); }}
                        className="text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40">Grant</button>
                    </>
                  )}
                  {ag.commission_status === 'Approved' && (
                    <button onClick={() => { setMsg(''); revokeAgent.mutate(ag.id); }} className="text-xs border border-border text-danger rounded px-3 py-1.5">Revoke</button>
                  )}
                </div>
              </div>
            ))}
            {(agents.data?.rows ?? []).length === 0 && <div className="p-6 text-center text-text-muted">No agents yet.</div>}
          </div>
        </>
      )}

    </div>
  );
}

/** Per-customer incentive breakdown for one payee, with pay-in-full per customer. */
function PayeeAccruals({ p, canPay, canRevert, onPaid, onReverted }: { p: Payee; canPay: boolean; canRevert: boolean; onPaid: () => void; onReverted: () => void }) {
  const { confirm } = useConfirm();
  const qc = useQueryClient();
  const key = ['inc-accruals', p.payee_type, p.payee_id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.get<{ rows: Accrual[] }>(`/api/incentives/payees/${p.payee_type}/${p.payee_id}/accruals`) });
  const payOne = useMutation({
    mutationFn: (applicationId: number) => api.post(`/api/incentives/payees/${p.payee_type}/${p.payee_id}/accruals/${applicationId}/pay`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); onPaid(); },
  });
  const revertOne = useMutation({
    mutationFn: (applicationId: number) => api.post(`/api/incentives/payees/${p.payee_type}/${p.payee_id}/accruals/${applicationId}/revert-payment`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); onReverted(); },
  });
  const rows = data?.rows ?? [];

  /**
   * Month-wise, above the per-customer list (owner 2026-09-07: "make the
   * incentives page in such a way that i get to see month wise data for me to
   * easily see each ones monthly incentives").
   *
   * The list below already carries a Month column, but 44 rows is not a monthly
   * figure — reading "what did Bindhu earn in August" off it meant adding rows
   * up by eye. Clicking a month filters the list to it.
   */
  const monthly = useQuery({
    queryKey: ['inc-monthly', p.payee_type, p.payee_id],
    queryFn: () => api.get<{ rows: MonthlyRow[] }>(`/api/incentives/payees/${p.payee_type}/${p.payee_id}/monthly`),
  });
  const [month, setMonth] = useState<string | null>(null);

  const monthCols: Column<MonthlyRow>[] = [
    { key: 'month', header: 'Month', tdClassName: 'font-semibold', value: (r) => r.month,
      render: (r) => (
        <button onClick={() => setMonth(month === r.month ? null : r.month)}
          className={`hover:underline ${month === r.month ? 'text-primary font-semibold' : 'text-primary'}`}>
          {monthLabel(`${r.month}-01`)}
        </button>
      ) },
    { key: 'investments', header: 'Investments', align: 'right', value: (r) => r.investments },
    { key: 'customers', header: 'Customers', align: 'right', value: (r) => r.customers },
    { key: 'investment_amount', header: 'Investment', align: 'right',
      value: (r) => Number(r.investment_amount), render: (r) => <span className="mono">{formatINR(r.investment_amount)}</span> },
    { key: 'accrued', header: 'Incentive', align: 'right',
      value: (r) => Number(r.accrued), render: (r) => <span className="mono font-semibold">{formatINR(r.accrued)}</span> },
    { key: 'paid', header: 'Paid', align: 'right',
      value: (r) => Number(r.paid), render: (r) => <span className="mono text-text-muted">{formatINR(r.paid)}</span> },
    { key: 'balance', header: 'Balance', align: 'right',
      value: (r) => Number(r.balance),
      render: (r) => <span className={`mono ${Number(r.balance) > 0 ? 'text-warn font-semibold' : 'text-text-muted'}`}>{formatINR(r.balance)}</span> },
    // Which side the month was earned on — it is the thing that explains a
    // figure back to the person who earned it.
    { key: 'split', header: 'Earned as', sortable: false, filterable: false,
      render: (r) => (
        <span className="text-xs text-text-muted whitespace-nowrap">
          {[r.as_enroller ? `${r.as_enroller} enrolled` : '', r.as_referrer ? `${r.as_referrer} referred` : ''].filter(Boolean).join(' · ') || '—'}
        </span>
      ) },
  ];

  const shown = month
    ? rows.filter((r) => String(r.date_money_received ?? '').slice(0, 7) === month)
    : rows;

  const cols: Column<Accrual>[] = [
    { key: 'customer', header: 'Customer', value: (r) => r.customer,
      render: (r) => <span>{r.customer} <span className="font-mono text-xs text-text-muted">{r.customer_code}</span></span> },
    { key: 'application_no', header: 'App no', tdClassName: 'font-mono text-xs' },
    { key: 'month', header: 'Month',
      value: (r) => r.date_money_received ? `${r.date_money_received.slice(0, 7)} ${monthLabel(r.date_money_received)}` : '',
      render: (r) => monthLabel(r.date_money_received) },
    { key: 'series_code', header: 'Series' },
    { key: 'investment_amount', header: 'Investment', align: 'right', value: (r) => Number(r.investment_amount), render: (r) => <span className="mono">{formatINR(r.investment_amount)}</span> },
    { key: 'incentive_amount', header: 'Incentive', align: 'right', value: (r) => Number(r.incentive_amount), render: (r) => <span className="mono font-semibold">{formatINR(r.incentive_amount)}</span> },
    { key: 'status', header: 'Status', align: 'right', sortable: false, filterable: false,
      render: (r) => r.paid ? (
        <span className="inline-flex items-center gap-2 justify-end whitespace-nowrap">
          <span className="text-xs text-success">Paid</span>
          {canRevert && <button disabled={revertOne.isPending}
            onClick={async () => { if (await confirm({ title: `Revert ${formatINR(r.incentive_amount)} to ${p.payee_name ?? 'this payee'}?`, body: `${r.customer} (${r.application_no}). It goes back to unpaid.`, confirmLabel: 'Revert', danger: true })) revertOne.mutate(r.application_id); }}
            className="text-xs border border-border text-danger rounded px-2 py-1 disabled:opacity-40 hover:bg-[color:var(--danger-bg)]">Revert</button>}
        </span>
      ) : canPay ? <button disabled={payOne.isPending}
            onClick={async () => { if (await confirm({ title: `Pay ${formatINR(r.incentive_amount)} to ${p.payee_name ?? 'this payee'}?`, body: `${r.customer} (${r.application_no}).`, confirmLabel: 'Mark paid' })) payOne.mutate(r.application_id); }}
            className="text-xs bg-primary text-white rounded px-3 py-1 disabled:opacity-40 hover:bg-primary-hover">Pay</button>
        : <span className="text-xs text-text-muted">Unpaid</span> },
  ];
  return (
    <div className="bg-bg p-3">
      <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1.5">
        Month-wise
        {month && <button onClick={() => setMonth(null)} className="ml-2 font-normal normal-case text-primary hover:underline">clear {monthLabel(`${month}-01`)}</button>}
      </div>
      <div className="mb-4">
        {monthly.isLoading
          ? <div className="text-xs text-text-muted px-2 py-3">Loading…</div>
          : <DataTable columns={monthCols} rows={monthly.data?.rows ?? []} rowKey={(r) => r.month}
              defaultSort={{ key: 'month', dir: 'desc' }} empty="Nothing accrued yet." />}
      </div>

      <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1.5">
        Customer-wise{month ? ` · ${monthLabel(`${month}-01`)}` : ''}
      </div>
      {isLoading ? <div className="text-xs text-text-muted px-2 py-3">Loading…</div> : (
        <DataTable columns={cols} rows={shown} rowKey={(r) => r.application_id}
          defaultSort={{ key: 'investment_amount', dir: 'desc' }}
          empty={month ? 'Nothing in that month.' : 'No eligible customers.'} />
      )}
    </div>
  );
}
